<template>
  <div class="fts-field-behavior-ledger">
    <!-- formula (value) -->
    <div
      class="form-formula-wrapper"
      v-if="(
        actions.indexOf('formula') > -1 &&
        ((formData.type !== 8 && formData.type !== 21 && formData.type !== 28) || isInternal)
      )"
    >
      <div class="form-formula-wrapper-header">
        <div class="form-formula-label">
          <i class="fts-icon-formula form-formula-label-icon"></i>
          Formula
          <template v-if="formData.type == 8 || formData.type == 21 || formData.type == 28">&nbsp;<fts-release-badge alpha /></template>
        </div>
      </div>

      <fts-formula-display
        :variables="variables"
        :form="form"
        :formula="informationFormula"
        v-if="informationFormula || isComputedField"
      />
      <!-- parent formula mode: the field's own formula is not editable here — show it inert -->
      <fts-formula-display
        :variables="variables"
        :form="form"
        :formula="formData.formula"
        v-else-if="isParentFormulaMode"
      />
      <!-- parentName is set only when the field is a table column, so it is also the
           column-context answer: without it FTSFormula filters rowAutoNumber out of every route
           (fnCandidates drops the row, fnConstants never reaches the columnOnly chip), which is
           how "Row Auto Number" went missing from this editor entirely (SS06017). -->
      <fts-formula
        :variables="variables"
        :form="form"
        engine-badge
        :activity-id="activityId"
        :parent-name="parentName"
        :column-context="Boolean(parentName)"
        property-name="formula"
        v-model="formDataFormula"
        v-else
      />
    </div>

    <!-- lock notice: one quiet line instead of blur overlays (§3.2b explain, don't blur) -->
    <div class="form-formula-notice" v-if="lockNotice">
      <font-awesome-icon :icon="['fas', 'lock']" class="form-formula-notice-icon" />
      {{ lockNotice }}
    </div>

    <!-- behavior rules: off / always / conditional as ledger text — weight, not hue -->
    <div class="form-formula-rules">
      <!-- required -->
      <div
        class="formula-rule-group"
        :class="{ 'formula-rule-group--locked': rulesLocked }"
        v-if="actions.indexOf('required') > -1"
      >
        <div class="formula-rule">
          <div class="form-formula-label">
            <i class="fts-icon-asterisk form-formula-label-icon"></i> Required
          </div>
          <div class="formula-rule-states">
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': requiredState === 'optional' }"
              @click="setRuleState('required', 'optional')"
            >Optional</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': requiredState === 'always' }"
              @click="setRuleState('required', 'always')"
            >Required</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': requiredState === 'when' }"
              @click="setRuleState('required', 'when')"
            >When…</span>
          </div>
        </div>
        <div class="formula-rule-editor" v-if="requiredState === 'when' && !rulesLocked">
          <fts-formula
            :variables="variables"
            :form="form"
            engine-badge
            :activity-id="activityId"
            :parent-name="parentName"
            :column-context="Boolean(parentName)"
            property-name="requiredFormula"
            v-model="formData.requiredFormula"
          />
        </div>
      </div>
      <!-- readonly -->
      <div
        class="formula-rule-group"
        :class="{ 'formula-rule-group--locked': rulesLocked || isComputedField }"
        v-if="actions.indexOf('readOnly') > -1"
      >
        <div class="formula-rule">
          <div class="form-formula-label">
            <i class="fts-icon-pencil-slash form-formula-label-icon"></i> View only
          </div>
          <div class="formula-rule-states">
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': readOnlyState === 'editable' }"
              @click="setRuleState('readOnly', 'editable')"
            >Editable</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': readOnlyState === 'always' }"
              @click="setRuleState('readOnly', 'always')"
            >View only</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': readOnlyState === 'when' }"
              @click="setRuleState('readOnly', 'when')"
            >When…</span>
          </div>
        </div>
        <div class="formula-rule-editor" v-if="readOnlyState === 'when' && !rulesLocked && !isComputedField">
          <fts-formula
            :variables="variables"
            :form="form"
            engine-badge
            :activity-id="activityId"
            :parent-name="parentName"
            :column-context="Boolean(parentName)"
            property-name="readOnlyFormula"
            v-model="formData.readOnlyFormula"
          />
        </div>
      </div>
      <!-- visible: never locked — it stays designable on computed fields too -->
      <div class="formula-rule-group" v-if="actions.indexOf('visible') > -1">
        <div class="formula-rule">
          <div class="form-formula-label">
            <i class="fts-icon-hidden form-formula-label-icon"></i> Visible
          </div>
          <div class="formula-rule-states">
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': visibleState === 'visible' }"
              @click="setRuleState('visible', 'visible')"
            >Visible</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': visibleState === 'hidden' }"
              @click="setRuleState('visible', 'hidden')"
            >Hidden</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': visibleState === 'when' }"
              @click="setRuleState('visible', 'when')"
            >When…</span>
          </div>
        </div>
        <div class="formula-rule-editor" v-if="visibleState === 'when'">
          <fts-formula
            :variables="variables"
            :form="form"
            engine-badge
            :activity-id="activityId"
            :parent-name="parentName"
            :column-context="Boolean(parentName)"
            property-name="visibleFormula"
            v-model="formData.visibleFormula"
          />
        </div>
      </div>
      <!-- §4.3.2 GNey auto-fill: FORM-level behavior of a photo/file field, beside its
           formulas — when this file changes, GNey fills the form per the instruction -->
      <div class="formula-rule-group" v-if="showAiFill">
        <div class="formula-rule">
          <div class="form-formula-label">
            <font-awesome-icon
              :icon="['fas', 'wand-magic-sparkles']"
              class="form-formula-label-icon"
            />
            Fill form with GNey <fts-release-badge alpha />
          </div>
          <div class="formula-rule-states">
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': !aiFillEnabled }"
              @click="setAiFillEnabled(false)"
            >Off</span>
            <span class="formula-rule-sep">·</span>
            <span
              class="formula-rule-state"
              :class="{ 'formula-rule-state--on': aiFillEnabled }"
              @click="setAiFillEnabled(true)"
            >On</span>
          </div>
        </div>
        <div class="formula-rule-editor" v-if="aiFillEnabled && formData.aiFill">
          <div class="ai-fill-hint">
            When this file changes, GNey reads it and fills the form — the user sees what was
            filled and can undo. Costs AI credits per change.
          </div>
          <textarea
            class="fts-form-control"
            rows="3"
            placeholder="e.g. fill the IC number and business address"
            v-model="formData.aiFill.instruction"
          ></textarea>
          <select
            class="fts-form-control ai-fill-seniority"
            v-model="formData.aiFill.seniority"
          >
            <option value="junior">Junior</option>
            <option value="senior">Senior</option>
            <option value="expert">Expert</option>
            <option value="consultant">Consultant</option>
          </select>
        </div>
      </div>
      <!-- host-provided extra rules (assignee / due / addable …) render after the core three -->
      <slot name="extra-rules" />
    </div>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';

import FTSFormula from './FTSFormula';
import FTSFormulaDisplay from './FTSFormulaDisplay';
import FTSReleaseBadge from '../elements/FTSReleaseBadge';

/* rule → the formData keys it owns */
const RULE_KEYS = {
  required: { flag: 'required', formula: 'requiredFormula' },
  readOnly: { flag: 'readOnly', formula: 'readOnlyFormula' },
  visible: { flag: 'visible', formula: 'visibleFormula' }
};

/* The behavior ledger (excel-formula-editor-proposal §3.2b): value formula + the three-state
   rows (off / always / conditional, leftmost = default). One component, two hosts — the form
   formula modal (edits a clone, Ok resolves) and the form designer's expanded row (edits the
   live element). It mutates `formData` directly; hosts own persistence. */
export default {
  components: {
    'fts-formula': FTSFormula,
    'fts-formula-display': FTSFormulaDisplay,
    'fts-release-badge': FTSReleaseBadge
  },
  props: {
    formData: { type: Object, required: true },
    variables: Array,
    form: Object,
    actions: { type: Array, default: () => [] },
    informationFormula: String,
    isComputed: Boolean,
    isParentFormulaMode: Boolean,
    activityId: [String, Number],
    parentName: String
  },
  data: () => ({
    /* per-rule "When…" editor visibility, and the stash that keeps a formula recoverable while
       the rule is switched to a non-conditional state — nothing is deleted until finalize(). */
    whenOpen: { required: false, readOnly: false, visible: false },
    stash: { required: '', readOnly: '', visible: '' }
  }),
  watch: {
    formData: {
      immediate: true,
      /* re-sync only when the ledger is pointed at a DIFFERENT field. Vue calls object watchers
         even when the value is identical, so this also fires for our own $set/$delete on the
         current field — the object's __ob__.dep notifies every subscriber that merely read
         `this.formData`, deep or not. Re-deriving there would clobber the whenOpen flag
         setRuleState just set (the formula it wrote is '' for a fresh rule, so Boolean(...) reads
         back false) and wipe the stash, which is what made "When…" need a second click (SS06023). */
      handler(next, previous) {
        if (previous && next === previous) return;

        this.whenOpen = {
          required: Boolean(this.formData.requiredFormula),
          readOnly: Boolean(this.formData.readOnlyFormula),
          visible: Boolean(this.formData.visibleFormula)
        };
        this.stash = { required: '', readOnly: '', visible: '' };

        /* §4.3.2 GNey auto-fill: file/image kinds get a bindable config object up front
           (enabled=false until flipped; prompt/seniority survive toggling off). */
        if (this.showAiFill) {
          if (!this.formData.aiFill) {
            this.$set(this.formData, 'aiFill', {
              enabled: false,
              instruction: '',
              seniority: 'junior'
            });
          } else if (!this.formData.aiFill.seniority) {
            this.$set(this.formData.aiFill, 'seniority', 'junior');
          }
        }
      }
    }
  },
  methods: {
    /* §4.3.2 GNey auto-fill: keep the config on toggle-off so re-enabling restores it */
    setAiFillEnabled(enabled) {
      if (!this.formData.aiFill) {
        this.$set(this.formData, 'aiFill', {
          enabled: enabled,
          instruction: '',
          seniority: 'junior'
        });
      } else {
        this.$set(this.formData.aiFill, 'enabled', enabled);
      }
    },
    /* one mutator for every rule row. Leaving "When…" stashes the formula (recoverable until
       finalize); entering it restores the stash. The static flag and the formula never coexist. */
    setRuleState(rule, state) {
      if (this.rulesLocked && rule !== 'visible') return;
      if (rule === 'readOnly' && this.isComputedField) return;

      const keys = RULE_KEYS[rule];
      const current = rule === 'required' ? this.requiredState
        : rule === 'readOnly' ? this.readOnlyState
        : this.visibleState;
      if (state === current) return;

      if (this.whenOpen[rule]) {
        this.stash[rule] = this.formData[keys.formula] || '';
        this.$delete(this.formData, keys.formula);
        this.whenOpen[rule] = false;
      }

      if (state === 'when') {
        this.$delete(this.formData, keys.flag);
        this.$set(this.formData, keys.formula, this.stash[rule]);
        this.whenOpen[rule] = true;
      } else if (rule === 'visible') {
        // visible's static flag is inverted: absent = visible, false = hidden
        if (state === 'hidden') {
          this.$set(this.formData, 'visible', false);
        } else {
          this.$delete(this.formData, 'visible');
        }
      } else if (state === 'always') {
        this.$set(this.formData, keys.flag, true);
      } else {
        this.$delete(this.formData, keys.flag);
      }
    },
    /* an empty "When…" condition is no condition — drop the key so the rule reads as off */
    finalize() {
      Object.keys(RULE_KEYS).forEach((rule) => {
        if (this.whenOpen[rule] && !this.formData[RULE_KEYS[rule].formula]) {
          this.$delete(this.formData, RULE_KEYS[rule].formula);
          this.whenOpen[rule] = false;
        }
      });

      /* §4.3.2: an untouched auto-initialized aiFill (off, no prompt) leaves no trace */
      const aiFill = this.formData.aiFill;
      if (aiFill && !aiFill.enabled && !(aiFill.instruction || '').trim()) {
        this.$delete(this.formData, 'aiFill');
      }
    }
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    /* §4.3.2 GNey auto-fill — a FORM-level behavior of file/image kinds (internal alpha) */
    showAiFill() {
      const kind = Number(this.formData && this.formData.type);
      return this.isInternal && [10, 11, 12, 13].indexOf(kind) > -1;
    },
    aiFillEnabled() {
      return !!(this.formData.aiFill && this.formData.aiFill.enabled);
    },
    formDataFormula: {
      get() {
        return this.formData.formula;
      },
      set(val) {
        if (!this.formData.formula && Boolean(val)) {
          this.$set(this.formData, "readOnly", true);
        }
        this.$set(this.formData, "formula", val);
      }
    },
    /* a computed (formula) field is always read-only — the state is locked at "View only".
       `computed` may be propagated onto the form element, or passed explicitly when resolved
       from the Information definition. */
    isComputedField() {
      return this.isComputed || !!(this.formData && this.formData.computed);
    },
    /* required + view-only are owned elsewhere in these modes */
    rulesLocked() {
      return this.isParentFormulaMode || this.isComputedField || Boolean(this.informationFormula);
    },
    lockNotice() {
      if (this.isComputedField)
        return 'Computed field — the formula lives on the Information definition, and computed fields are always view only.';
      if (this.isParentFormulaMode || this.informationFormula)
        return 'Information formula mode — required and view only are controlled by the information formula.';
      return null;
    },
    requiredState() {
      if (this.whenOpen.required) return 'when';
      return this.formData.required ? 'always' : 'optional';
    },
    readOnlyState() {
      if (this.isComputedField) return 'always';
      if (this.whenOpen.readOnly) return 'when';
      return this.formData.readOnly ? 'always' : 'editable';
    },
    visibleState() {
      if (this.whenOpen.visible) return 'when';
      return this.formData.visible === false ? 'hidden' : 'visible';
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

/* calm density (excel-formula-editor-proposal §3.2b): the value slot keeps its quiet label
   line + boxed editor bar; behavior rules are one-line ledger rows whose active state carries
   weight, not hue — a conditional rule opens its editor bar beneath the row. Unscoped under
   the component class so host-provided extra rule rows share the vocabulary. */
.fts-field-behavior-ledger {
  .form-formula-wrapper {
    margin-bottom: 0.25rem;
  }
  /* §4.3.2 GNey auto-fill editor */
  .ai-fill-hint {
    margin-bottom: 0.375rem;
    font-size: 0.75rem;
    opacity: 0.75;
  }
  .ai-fill-seniority {
    margin-top: 0.375rem;
    max-width: 12rem;
  }
  .form-formula-wrapper-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.3rem;
  }
  .form-formula-label {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    line-height: $line-height;
    color: var(--fts-form-label);
    .form-formula-label-icon {
      margin-right: 0.4rem;
      font-size: 0.75rem;
      opacity: 0.7;
    }
  }
  .form-formula-notice {
    display: flex;
    align-items: center;
    margin-top: 0.625rem;
    padding: 0.375rem 0.625rem;
    border-radius: 0.25rem;
    background-color: var(--fts-gray-100);
    color: var(--fts-default);
    font-size: 0.75rem;
    line-height: 1rem;
    .form-formula-notice-icon {
      margin-right: 0.5rem;
      font-size: 0.625rem;
      opacity: 0.55;
    }
  }
  .form-formula-rules {
    margin-top: 0.75rem;
    border-top: 1px solid var(--fts-border);
  }
  .formula-rule-group {
    border-bottom: 1px solid var(--fts-border);
    &:last-child {
      border-bottom: none;
    }
    &.formula-rule-group--locked {
      .form-formula-label {
        opacity: 0.7;
      }
      .formula-rule-states {
        opacity: 0.5;
        pointer-events: none;
      }
    }
  }
  .formula-rule {
    display: flex;
    align-items: center;
    padding: 0.625rem 0;
  }
  .formula-rule-states {
    @include user-select-none;
    margin-left: auto;
    display: flex;
    align-items: baseline;
    font-size: 0.75rem;
    line-height: $line-height;
  }
  .formula-rule-state {
    color: var(--fts-default);
    cursor: pointer;
    &:hover {
      color: var(--fts-text);
    }
    &.formula-rule-state--on {
      color: var(--fts-text);
      font-weight: 600;
      cursor: default;
    }
  }
  .formula-rule-sep {
    padding: 0 0.5rem;
    color: var(--fts-border);
  }
  .formula-rule-editor {
    padding-bottom: 0.75rem;
  }
}
</style>
